import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";

export interface ReportsOverviewKpis {
  agendados: number;
  confirmados: number;
  finalizados: number;
  pagos: number;
  receitaTotal: number;
}

export interface RevenueByPeriodPoint {
  period: string;
  receita: number;
  label: string;
}

export interface AppointmentsBarPoint {
  period: string;
  agendados: number;
  confirmados: number;
  finalizados: number;
  pagos: number;
  label: string;
}

type AppointmentFinancialSelect = { status?: string | null } | Array<{ status?: string | null }> | null | undefined;

const extractFinancialStatus = (financial: AppointmentFinancialSelect): string | null => {
  if (!financial) return null;
  if (Array.isArray(financial)) return financial[0]?.status ?? null;
  return financial.status ?? null;
};

/** Funnel operacional + financeiro (pagos). Excludes cancelado/faltou. */
function funnelCounts(
  status: string,
  financialStatus: string | null,
): { agendados: number; confirmados: number; finalizados: number; pagos: number } {
  if (status === "cancelado" || status === "faltou") {
    return { agendados: 0, confirmados: 0, finalizados: 0, pagos: 0 };
  }
  const agendados = 1;
  const normalizedStatus = status === "pago" ? "finalizado" : status;
  const confirmados = ["confirmado", "em_atendimento", "finalizado"].includes(normalizedStatus) ? 1 : 0;
  const finalizados = normalizedStatus === "finalizado" ? 1 : 0;
  const pagamentos = deriveFinancialStatusWithLegacyFallback(
    financialStatus ? { status: financialStatus } : null,
    { status, grooming_status: null },
  );
  const pagos = pagamentos === "pago" ? 1 : 0;
  return { agendados, confirmados, finalizados, pagos };
}

export interface ReportsOverviewData {
  kpis: ReportsOverviewKpis;
  revenueLine: RevenueByPeriodPoint[];
  appointmentsBar: AppointmentsBarPoint[];
}

function getDateRange(dateFrom: Date, dateTo: Date, granularity: "day" | "week" | "month") {
  const points: { key: string; label: string }[] = [];
  const from = new Date(dateFrom);
  const to = new Date(dateTo);

  if (granularity === "day") {
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      points.push({
        key: format(d, "yyyy-MM-dd"),
        label: format(d, "dd/MM", { locale: ptBR }),
      });
    }
  } else if (granularity === "week") {
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 7)) {
      points.push({
        key: format(d, "yyyy-'W'ww"),
        label: `Sem ${format(d, "dd/MM", { locale: ptBR })}`,
      });
    }
  } else {
    for (let d = new Date(from.getFullYear(), from.getMonth(), 1); d <= to; d.setMonth(d.getMonth() + 1)) {
      points.push({
        key: format(d, "yyyy-MM"),
        label: format(d, "MMM yyyy", { locale: ptBR }),
      });
    }
  }
  return points;
}

export function useReportsOverview(dateFrom: Date | null, dateTo: Date | null) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery({
    queryKey: ["reports-overview", clinicId, dateFrom?.toISOString(), dateTo?.toISOString()],
    queryFn: async (): Promise<ReportsOverviewData> => {
      if (!clinicId || !dateFrom || !dateTo) {
        return {
          kpis: { agendados: 0, confirmados: 0, finalizados: 0, pagos: 0, receitaTotal: 0 },
          revenueLine: [],
          appointmentsBar: [],
        };
      }

      const fromIso = dateFrom.toISOString();
      const toIso = dateTo.toISOString();
      const daysDiff = Math.ceil((dateTo.getTime() - dateFrom.getTime()) / (1000 * 60 * 60 * 24));
      const granularity: "day" | "week" | "month" =
        daysDiff <= 31 ? "day" : daysDiff <= 93 ? "week" : "month";

      const [appointmentsRes, salesRes] = await Promise.all([
        supabase
          .from("appointments")
          .select("id, status, starts_at, appointment_financial:appointment_financials(status)")
          .eq("clinic_id", clinicId)
          .gte("starts_at", fromIso)
          .lte("starts_at", toIso),
        supabase
          .from("sales")
          .select("id, total, created_at")
          .eq("clinic_id", clinicId)
          .eq("status", "PAGA")
          .gte("created_at", fromIso)
          .lte("created_at", toIso),
      ]);

      if (appointmentsRes.error) throw appointmentsRes.error;
      if (salesRes.error) throw salesRes.error;

      const appointments = appointmentsRes.data ?? [];
      const sales = salesRes.data ?? [];

      let agendados = 0;
      let confirmados = 0;
      let finalizados = 0;
      let pagos = 0;
      appointments.forEach((a) => {
        const row = a as unknown as { status?: string | null; appointment_financial?: AppointmentFinancialSelect };
        const f = funnelCounts(row.status ?? "", extractFinancialStatus(row.appointment_financial));
        agendados += f.agendados;
        confirmados += f.confirmados;
        finalizados += f.finalizados;
        pagos += f.pagos;
      });
      const receitaTotal = sales.reduce((sum, s) => sum + Number(s.total ?? 0), 0);

      const periodPoints = getDateRange(dateFrom, dateTo, granularity);

      const revenueByKey: Record<string, number> = {};
      const barByKey: Record<string, { agendados: number; confirmados: number; finalizados: number; pagos: number }> = {};
      periodPoints.forEach((p) => {
        revenueByKey[p.key] = 0;
        barByKey[p.key] = { agendados: 0, confirmados: 0, finalizados: 0, pagos: 0 };
      });

      const getKey = (d: Date) =>
        granularity === "day"
          ? format(d, "yyyy-MM-dd")
          : granularity === "week"
            ? format(d, "yyyy-'W'ww")
            : format(d, "yyyy-MM");

      sales.forEach((s) => {
        const key = getKey(new Date(s.created_at!));
        if (revenueByKey[key] !== undefined) revenueByKey[key] += Number(s.total ?? 0);
      });
      appointments.forEach((a) => {
        const key = getKey(new Date(a.starts_at));
        if (barByKey[key]) {
          const row = a as unknown as { status?: string | null; appointment_financial?: AppointmentFinancialSelect };
          const f = funnelCounts(row.status ?? "", extractFinancialStatus(row.appointment_financial));
          barByKey[key].agendados += f.agendados;
          barByKey[key].confirmados += f.confirmados;
          barByKey[key].finalizados += f.finalizados;
          barByKey[key].pagos += f.pagos;
        }
      });

      const revenueLine: RevenueByPeriodPoint[] = periodPoints.map((p) => ({
        period: p.key,
        label: p.label,
        receita: revenueByKey[p.key] ?? 0,
      }));

      const appointmentsBar: AppointmentsBarPoint[] = periodPoints.map((p) => ({
        period: p.key,
        label: p.label,
        ...barByKey[p.key],
      }));

      return {
        kpis: { agendados, confirmados, finalizados, pagos, receitaTotal },
        revenueLine,
        appointmentsBar,
      };
    },
    enabled: !!clinicId && !!dateFrom && !!dateTo,
  });
}
